import React, { createContext, ReactNode, useCallback, useContext, useMemo } from 'react';
import {
  PageLoader,
  useFeatureFlags,
  useCurrentClientManager,
  useClientTokenManager,
  AccountLevel,
  Entity,
  isProductionEnvironment,
} from '@cko/dashboard-shared';

import { useGetNotificationsAvailability } from '../../hooks/useGetNotificationsAvailability';
import { useGetPrismLinkAvailability } from '../../hooks/useGetPrismLinkAvailability';
import { useGetIntelligentAcceptanceAvailability } from '../../hooks/useGetIntelligentAcceptanceAvailability';
import { useIsNetworkTokensOnboardedMerchant } from '../../hooks/useIsNetworkTokensOnboardedMerchant';
import { UserContext } from '../../config/shared/types/userContext';
import { FeatureFlag } from '../../config/shared/types/featureFlags';
import { Permission } from '../../config/shared/enums/permissions';
import { CustomCheck } from '../../config/shared/types/customCheck';

type ValidationType = 'ROUTE' | 'VIEW' | 'PAGE' | 'REDIRECT';

type ValidationFunction = (context: UserContext, validationType: ValidationType) => boolean;

type UserContextValidationProviderContextType = {
  validate: ValidationFunction;
};

const Context = createContext<UserContextValidationProviderContextType | undefined>(undefined);

export const useUserContextValidation = (): UserContextValidationProviderContextType => {
  const contextValue: UserContextValidationProviderContextType | undefined = useContext(Context);
  if (!contextValue) {
    throw new Error('useUserContextValidation must be used inside a UserContextValidationProvider');
  } else {
    return contextValue;
  }
};

export const UserContextValidationProvider = ({ children }: { children: ReactNode }) => {
  const { getFeatureFlag } = useFeatureFlags();
  const { subEntity, client } = useCurrentClientManager();

  const {
    getPrismLinkAvailability,
    isError: isPrismError,
    isLoading: isPrismLoading,
  } = useGetPrismLinkAvailability({ enabled: !subEntity });

  const {
    areIntelligentAcceptanceLinksAvailable,
    isError: isIntelligentAcceptanceError,
    isLoading: isIntelligentAcceptanceLoading,
  } = useGetIntelligentAcceptanceAvailability({
    enabled: !subEntity,
  });

  const {
    isNetworkTokensOnboardedMerchant: isNetworkTokensLinkAvailable,
    isError: isNetworkTokensError,
    isLoading: isNetworkTokensLoading,
  } = useIsNetworkTokensOnboardedMerchant({
    enabled: Boolean(client?.id),
  });

  const { isNotificationsAvailable } = useGetNotificationsAvailability();

  const {
    loading: isClientTokenLoading,
    isSegmentsOnlyUser,
    tokenProperties,
  } = useClientTokenManager();

  const isLoading = useMemo(
    () =>
      isPrismLoading ||
      isIntelligentAcceptanceLoading ||
      isClientTokenLoading ||
      isNetworkTokensLoading,
    [isPrismLoading, isIntelligentAcceptanceLoading, isClientTokenLoading, isNetworkTokensLoading],
  );

  const validateFeatureFlags = useCallback(
    (featureFlags: Array<FeatureFlag>): boolean => {
      let valid = true;
      featureFlags.forEach((flag: FeatureFlag) => {
        const flagValue = getFeatureFlag(flag.flag);
        if (flag.type === 'enabled' && !flagValue) {
          valid = false;
        }
        if (flag.type === 'disabled' && flagValue) {
          valid = false;
        }
      });
      return valid;
    },
    [getFeatureFlag],
  );

  const validateSegmentsOnlyUser = useCallback(
    (required_account_levels: Array<AccountLevel>): boolean => {
      if (!isSegmentsOnlyUser || required_account_levels.length === 0) {
        return true;
      }
      return required_account_levels.includes('segment');
    },
    [isSegmentsOnlyUser],
  );

  const validatePermissions = useCallback(
    (configPermissions: Array<Permission>): boolean => {
      const arePermissionsRequired = configPermissions.length > 0;
      if (!arePermissionsRequired) {
        return true;
      }

      const userTokenPermissions = new Set(tokenProperties.permissions);

      const hasAtLeastOneConfigPermission = configPermissions.some((configPermission) =>
        userTokenPermissions.has(configPermission),
      );

      return hasAtLeastOneConfigPermission;
    },
    [tokenProperties],
  );

  const validateCustomChecks = useCallback(
    (customChecks: Array<CustomCheck>): boolean => {
      //cannot return out of a foreach need to change
      if (customChecks.length === 0) {
        return true;
      }
      const checksPassed = customChecks.filter((check: CustomCheck) => {
        if (check.type === 'prism_link_availability') {
          if (isPrismLoading) {
            return false;
          }

          if (isPrismError) {
            return false;
          }
          return Boolean(
            getPrismLinkAvailability({
              disableForSubMerchants: check.disable_for_sub_merchants,
              premiumAccessOnly: check.premium_access_only,
            }),
          );
        }

        if (check.type === 'intelligent_acceptance') {
          if (isIntelligentAcceptanceError) {
            return false;
          }

          if (isIntelligentAcceptanceLoading) {
            return false;
          }

          return Boolean(areIntelligentAcceptanceLinksAvailable);
        }

        if (check.type === 'prod_environment') {
          return !isProductionEnvironment();
        }

        if (check.type === 'onboards_sub_entities') {
          return Boolean(client?.entities.some((ent: Entity) => ent.onboardsSubEntities === true));
        }

        if (check.type === 'network_tokens_link_availability') {
          if (isNetworkTokensLoading || isNetworkTokensError) {
            return false;
          }

          return isNetworkTokensLinkAvailable;
        }

        if (check.type === 'notifications_availability') {
          return isNotificationsAvailable;
        }

        return true;
      });

      return checksPassed.length === customChecks.length;
    },
    [
      getPrismLinkAvailability,
      areIntelligentAcceptanceLinksAvailable,
      isPrismLoading,
      isIntelligentAcceptanceLoading,
      isIntelligentAcceptanceError,
      isPrismError,
      client,
    ],
  );

  const validate = useCallback(
    (userContext: UserContext, validationType: ValidationType) => {
      if (userContext.feature_flags) {
        const featureFlagsValid = validateFeatureFlags(userContext.feature_flags);
        if (!featureFlagsValid) {
          return false;
        }
      }

      // Pages or Redirects are validated against feature flags only.
      if (validationType === 'PAGE' || validationType === 'REDIRECT') {
        return true;
      }

      if (userContext.permissions) {
        const permissionsValid = validatePermissions(userContext.permissions);
        if (!permissionsValid) {
          return false;
        }
      }

      // Views are validated against feature flags and permissions.
      if (validationType === 'VIEW') {
        return true;
      }

      if (userContext.required_account_levels) {
        const validSegmentsOnlyUser = validateSegmentsOnlyUser(userContext.required_account_levels);
        if (!validSegmentsOnlyUser) {
          return false;
        }
      }

      if (userContext.custom_checks) {
        const customChecksValid = validateCustomChecks(userContext.custom_checks);

        if (!customChecksValid) {
          return false;
        }
      }

      return true;
    },
    [validateFeatureFlags, validateSegmentsOnlyUser, validatePermissions, validateCustomChecks],
  );

  const value = useMemo(
    () => ({
      validate,
    }),
    [validate],
  );

  return isLoading ? <PageLoader /> : <Context.Provider value={value}>{children}</Context.Provider>;
};
